/**
 * Tipos compartidos para el pipeline de detección de pose.
 *
 * No importa de @mediapipe/tasks-vision directamente — estos tipos son
 * un abstracción propia que permite cambiar de librería (MediaPipe → MoveNet)
 * sin tocar el resto del código.
 */

/** Índices de keypoints según MediaPipe Pose (33 total).
 *  Subset usado por los ejercicios actuales. Indices < 11 son face
 *  landmarks — para cuello (Sprint 3 spec-medicion Bloque 4) usamos las
 *  orejas (7, 8) como landmark principal y ojos (2, 5) como fallback si
 *  la visibilidad de las orejas es floja en calibración (pelo, ángulo). */
export const KP = {
  NOSE: 0,
  // Face landmarks — cuello inclinación lateral
  LEFT_EYE: 2,
  RIGHT_EYE: 5,
  LEFT_EAR: 7,
  RIGHT_EAR: 8,
  // Upper body — rot_externa_banda
  LEFT_SHOULDER: 11,
  RIGHT_SHOULDER: 12,
  LEFT_ELBOW: 13,
  RIGHT_ELBOW: 14,
  LEFT_WRIST: 15,
  RIGHT_WRIST: 16,
  // Hip — usado por ambos ejercicios
  LEFT_HIP: 23,
  RIGHT_HIP: 24,
  // Lower body — sentadilla_mini_conservadora
  LEFT_KNEE: 25,
  RIGHT_KNEE: 26,
  LEFT_ANKLE: 27,
  RIGHT_ANKLE: 28,
  // Lower body P1 (talón / pie) — diferidos a Sprint 3+ pero constantes baratas
  LEFT_HEEL: 29,
  RIGHT_HEEL: 30,
  LEFT_FOOT_INDEX: 31,
  RIGHT_FOOT_INDEX: 32,
} as const;

export type KeypointIndex = (typeof KP)[keyof typeof KP];

/** Un keypoint detectado por el modelo.
 *  x, y son coordenadas normalizadas [0..1] relativas al frame.
 *  z es profundidad estimada (solo MediaPipe 3D — negativo = más cerca de cámara).
 *  visibility indica confianza [0..1] — si < 0.5 ignorar el keypoint. */
export interface Keypoint {
  x: number;
  y: number;
  z: number;
  visibility: number;
}

/** Resultado de un frame completo: los 33 keypoints + metadata. */
export interface PoseResult {
  keypoints: Keypoint[];
  /** Timestamp del frame en milisegundos. */
  timestamp: number;
  /** FPS instantáneo para diagnóstico. */
  fps: number;
  /** Dimensiones intrínsecas del stream de cámara (px). Usadas por
   *  reglas que necesitan convertir coords normalizadas [0..1] a
   *  pixels para calcular ángulos aspect-ratio-independientes (ej.
   *  vertical_alignment_max_deg). Opcional para retrocompat con
   *  callers antiguos; si vienen 0/undefined el fallback usa las
   *  coords normalizadas (distorsión aceptable en portrait/landscape
   *  similares, pero el umbral hay que recalibrar). */
  videoWidth?: number;
  videoHeight?: number;
}

// ============================================================================
// Exercise schema — lo que se carga del JSON de cada ejercicio
// ============================================================================

/** Una regla del ejercicio: algo que tiene que cumplirse durante la rep.
 *  Si no se cumple, se dispara una violación con su mensaje de feedback.
 *
 *  `feedback` se muestra al paciente DURANTE el ejercicio (imperativo:
 *  "Mantené el codo a 90°"). `summaryLabel` es opcional y se usa en el
 *  resumen post-sesión, en pasado/descriptivo ("El codo no estuvo a 90°").
 *  Si no se provee, la UI cae a `feedback` como fallback. */
export type ExerciseRule =
  | {
      type: "angle_range";
      /** Los 3 keypoints que forman el ángulo (vértice en el del medio). */
      vertex: KeypointIndex;
      a: KeypointIndex;
      b: KeypointIndex;
      min: number;
      max: number;
      violationId: string;
      feedback: string;
      summaryLabel?: string;
    }
  | {
      type: "horizontal_distance_max";
      kpA: KeypointIndex;
      kpB: KeypointIndex;
      /** Máxima distancia horizontal permitida (en unidades normalizadas [0..1]). */
      maxDistance: number;
      violationId: string;
      feedback: string;
      summaryLabel?: string;
    }
  | {
      type: "trunk_stability";
      /** Variación máxima del ángulo del torso durante la rep (grados). */
      maxVariationDeg: number;
      violationId: string;
      feedback: string;
      summaryLabel?: string;
    }
  | {
      /** Desviación del vector (kpTop → kpBottom) respecto a la vertical
       *  del frame, medida en grados. Uso principal: verificar que el
       *  brazo superior (hombro → codo) esté a PLOMO del hombro (no
       *  flareado hacia afuera). Reemplaza el modelo previo con
       *  horizontal_distance_max (kpA=codo, kpB=cadera) que era
       *  clínicamente incorrecto — el cuerpo humano es trapecio
       *  (hombros anchos, cadera angosta), así que en sujetos con hombros
       *  anchos el codo cae naturalmente lateral a la cadera aunque el
       *  brazo esté correctamente pegado al torso.
       *
       *  Fórmula (pixels reales para invariancia de aspect ratio):
       *    dxPx = (kpBottom.x - kpTop.x) * videoWidth
       *    dyPx = (kpBottom.y - kpTop.y) * videoHeight   (Y ↓)
       *    ángulo = atan2(|dxPx|, dyPx) * 180/π
       *  Rango: 0° (vector apunta abajo, plomo perfecto) a 180°.
       *  Codo a plomo del hombro → ~0-10°. Codo flareado afuera → >20°.
       *  IMPORTANTE: coords MediaPipe se normalizan separadamente por
       *  ancho/alto del stream, así que atan2 sobre normalized crudo
       *  distorsiona el ángulo según aspect ratio. La escala a pixels
       *  antes del atan2 hace el umbral invariante entre desktop
       *  (landscape) y celular (portrait). Requiere pose.videoWidth /
       *  videoHeight del PoseResult; fallback a normalized (previo)
       *  si vienen 0 en warm-up.
       *  Corrección aportada por Ale (kinesiólogo) 7/8/2026. Juan valida
       *  el umbral final en clinical review. */
      type: "vertical_alignment_max_deg";
      kpTop: KeypointIndex;
      kpBottom: KeypointIndex;
      maxDeviationDeg: number;
      violationId: string;
      feedback: string;
      summaryLabel?: string;
    };

/** Dirección del movimiento esperado del ángulo del repDetector durante
 *  la fase concéntrica.
 *
 *  - "increasing" (default, retrocompat): el ángulo CRECE durante la
 *    concéntrica. Usado por rotación externa (la mano se aleja del
 *    cuerpo → apertura angular aumenta).
 *  - "decreasing": el ángulo DECRECE durante la concéntrica. Usado por
 *    rotación interna (la mano se acerca al cuerpo → apertura angular
 *    disminuye).
 *
 *  Los thresholds `lowThreshold` / `highThreshold` se interpretan como
 *  los EXTREMOS del rango, independientemente de la dirección. Para
 *  "decreasing", el caller mantiene los MISMOS valores numéricos que
 *  "increasing" — la inversión la hace el evaluator. */
export type RepDirection = "increasing" | "decreasing";

/** Detector de repeticiones: define cómo contar cuándo una rep empieza y
 *  termina. Union discriminado por `type` — permite semánticas distintas
 *  (oscilación angular vs inclinación lateral bilateral) sin metricar
 *  ambas con la misma máquina de estados. */
export type RepDetector = OscillatingAngleDetector | NeckLateralFlexionDetector;

/** Detector legacy — mide la oscilación de un ángulo entre 2 umbrales.
 *  Usado por rot_externa_banda, rot_interna_banda y sentadilla_mini
 *  (aunque sentadilla lo declara pero el SquatEvaluator lo ignora). */
export interface OscillatingAngleDetector {
  type: "oscillating_angle";
  vertex: KeypointIndex;
  a: KeypointIndex;
  b: KeypointIndex;
  /** Ángulo del extremo "inicio" del rango (posición relajada). Para
   *  "increasing", es el límite inferior. Para "decreasing", el superior. */
  lowThreshold: number;
  /** Ángulo del extremo "pico" del rango (rep completada). Para
   *  "increasing", es el límite superior. Para "decreasing", el inferior. */
  highThreshold: number;
  /** Milisegundos que hay que mantener cada umbral para validarlo
   *  (evita falsos positivos por temblor). */
  dwellMs: number;
  /** Dirección del movimiento durante la fase concéntrica. Default
   *  "increasing" si está ausente (retrocompat con rot_externa_banda
   *  y sentadilla_mini_conservadora que no declaran el campo). */
  direction?: RepDirection;
}

/** Detector de inclinación lateral de cuello — Sprint 3 spec-medicion
 *  Bloque 4 (12/7/2026). Mide el ángulo del vector midpoint(orejas) →
 *  midpoint(hombros) respecto a la vertical del frame. Signo del ángulo
 *  discrimina lado (positivo = izquierda del frame, negativo = derecha).
 *
 *  Modo "alternating": cuenta cada inclinación (L o R) como 1 rep. El
 *  spec ratificado por Ale pone repsObjetivo = 10 = 5 por lado, pero no
 *  fuerza alternancia — el paciente puede hacer L L R R o R L R L y todas
 *  cuentan. La distribución final la reporta el summary post-sesión.
 *  Cómo contar reglas de forma es punto de validación clínica con Juan
 *  (spec-medicion-ejercicios.md Q3). */
export interface NeckLateralFlexionDetector {
  type: "neck_lateral_flexion";
  /** Landmark superior del vector cabeza (KP.LEFT_EAR / KP.RIGHT_EAR).
   *  Fallback a ojos (KP.LEFT_EYE / KP.RIGHT_EYE) si la visibilidad de
   *  las orejas es floja en calibración — el spec Q2 lo permite pero se
   *  arranca con orejas por defecto. */
  headA: KeypointIndex;
  headB: KeypointIndex;
  /** Landmarks de los hombros (base del vector). */
  shoulderA: KeypointIndex;
  shoulderB: KeypointIndex;
  /** Ángulo (en grados desde vertical) que hay que superar para contar
   *  como pico de inclinación lateral. Placeholder por defecto — calibrar
   *  con Ale de sujeto. */
  thresholdDeg: number;
  /** Ancho de la "zona neutra" alrededor de 0° donde se resetea entre reps.
   *  |ángulo| < neutralZoneDeg = considerado neutro. */
  neutralZoneDeg: number;
  /** Ms que hay que mantener por encima del threshold para validar el pico. */
  dwellMs: number;
  /** "alternating" = cuenta L o R indistintamente. Único modo por ahora;
   *  reservado para futuros modos ("single_side_left/right"). */
  mode: "alternating";
}

/** Discriminator del shape del schema. Cuando es undefined o
 *  "oscillating_angle", el schema sigue el flow legacy de rot_externa_banda
 *  (rules + repDetector oscilante, evaluator binario). Cuando es
 *  "squat_phases", el schema tiene shape extendido (baseline + tempo +
 *  3-level classification + phases lineales) y va al SquatEvaluator.
 *  Cuando es "neck_lateral_flexion" (Sprint 3 spec-medicion Bloque 4),
 *  el schema declara un repDetector distinto (mide inclinación bilateral
 *  del vector cabeza→hombros) y va al NeckLateralFlexionEvaluator. */
export type ExerciseSchemaType =
  | "oscillating_angle"
  | "squat_phases"
  | "neck_lateral_flexion";

/** Modo de tracking del ejercicio. "camera" = MediaPipe + detección
 *  estándar (default histórico, backward compat). "video_only" = el
 *  paciente reproduce un video tutorial y marca manualmente cuando
 *  terminó. No requiere cámara ni evaluator. */
export type TrackingMode = "camera" | "video_only";

/** Estado de calibración clínica del schema. "preliminary" = los
 *  thresholds del detector/clasificación están calibrados empíricamente
 *  pero no fueron validados por un kinesiólogo certificado contra
 *  población amplia. "reviewed" = pasó revisión clínica formal.
 *  Default conservador = "preliminary" cuando el campo falta.
 *  UI muestra badge amarillo cuando preliminary para transparencia
 *  con el kine durante el piloto. */
export type ClinicalReviewStatus = "preliminary" | "reviewed";

export interface ExerciseSchema {
  /** Discriminator opcional. Default "oscillating_angle" — rot_externa_banda
   *  no lo tiene en JSON y eso es OK (backward compat). Nuevos schemas
   *  con shape extendido (sentadilla, etc.) lo declaran explícitamente. */
  type?: ExerciseSchemaType;
  id: string;
  /** Versión semántica del schema. Se persiste en pose_analysis.detector
   *  para trazabilidad: qué versión del schema procesó qué sesión. Subir
   *  cuando cambien rules, repDetector thresholds o estructura del schema. */
  version: string;
  nombre: string;
  articulacion: string;
  descripcion: string;
  repsObjetivo: number;
  seriesObjetivo: number;
  /** Required para schemas legacy (oscillating). Opcional / vacío para
   *  squat_phases (la clasificación va por `classification`) y
   *  tracking_mode "video_only" (sin detección). */
  rules: ExerciseRule[];
  /** Required para schemas legacy y squat_phases. Opcional para
   *  tracking_mode "video_only" (no aplica). */
  repDetector?: RepDetector;
  /** Modo de tracking. Default "camera" cuando undefined (backward compat
   *  con schemas previos al PR1 de catálogo expandido). */
  tracking_mode?: TrackingMode;
  /** URL del video tutorial de YouTube. Opcional. Se muestra en biblioteca
   *  (thumbnail hqdefault.jpg) y, para tracking_mode="video_only", se
   *  embebe como iframe en la sesión del paciente. */
  video_url?: string;
  /** Estado de revisión clínica. Default "preliminary" cuando undefined.
   *  Drive el badge amarillo de transparencia en BibliotecaEjercicios. */
  clinical_review_status?: ClinicalReviewStatus;
  // ── Campos opcionales del shape extendido (squat_phases) ──
  /** Vista de cámara recomendada. "lateral" para sentadilla,
   *  "frontal" para rot externa. UI usa esto para el setup hint visual. */
  viewAngle?: "frontal" | "lateral" | "flexible";
  /** Texto descriptivo de cómo posicionarse — usado en setup guide
   *  condicional. */
  setupHint?: string;
  /** Configuración de captura de baseline pre-sesión. Solo presente para
   *  schemas que requieren calibración individual (sentadilla mini). */
  baseline?: BaselineConfig;
  /** Umbrales de tempo por fase. Solo presente para schemas con tempo
   *  tracking (sentadilla mini). */
  tempo?: TempoThresholds;
  /** Configuración de clasificación 3-level (ideal / acceptable / invalid).
   *  Solo presente para schemas que usan modelo 3-level en lugar de
   *  binario válida/inválida. */
  classification?: SquatClassificationConfig;
}

// ── Tipos del shape extendido (squat_phases) ──────────────────────────────

/** Config de captura de baseline pre-sesión. El evaluator promedia los
 *  ángulos durante `durationMs` y valida que estén en rango plausible
 *  (gate de "standing real") antes de permitir arrancar la sesión. */
export interface BaselineConfig {
  durationMs: number;
  /** Gate de standing real — flexión MÁXIMA de rodilla (en grados,
   *  0° = pierna extendida) para confirmar que el paciente está parado.
   *  Por encima de este umbral, el baseline se rechaza y se pide al
   *  paciente que se pare mejor. Equivalente a "knee_angle medido ≥ 150°"
   *  cuando el threshold es 30° de flexión (180 - 30 = 150°). */
  standingMaxKneeFlexion: number;
  /** Gate de standing real — trunk lean máximo (desde vertical) para
   *  confirmar postura erguida. */
  standingMaxTrunkLean: number;
}

/** Snapshot del baseline capturado, persistido en pose_analysis.baseline.
 *  Los thresholds del classification se aplican como delta desde este
 *  snapshot — no como valores absolutos. */
export interface BaselineSnapshot {
  kneeFlexion: number;
  hipFlexion: number;
  trunkAngle: number;
  visibilityScore: number;
  capturedAt: number;
}

/** Umbrales de tempo en ms. Per-fase + total de la rep. Versión simple
 *  del doc sección 6.2 — solo bloqueamos las reps claramente apuradas
 *  o rebotadas. Valores ajustables en QA físico (ver iter1 doc post-QA). */
export interface TempoThresholds {
  descentMinMsWarning: number;
  descentMinMsCritical: number;
  bottomHoldMinMsWarning: number;
  bottomHoldMinMsCritical: number;
  ascentMinMsWarning: number;
  ascentMinMsCritical: number;
  totalMinMsWarning: number;
  totalMinMsCritical: number;
}

/** Config de clasificación 3-level. Cada métrica define su target zone +
 *  acceptable zone. Out of range = invalid. La combinación se resuelve
 *  en el evaluator: invalid en cualquier métrica → rep invalid; warning
 *  en alguna → acceptable; todas en target → ideal. */
export interface SquatClassificationConfig {
  knee: {
    /** Rango ideal de profundidad en grados de flexión, relativo al baseline. */
    idealMin: number;
    idealMax: number;
    /** Rango aceptable — fuera de ideal pero clínicamente OK. */
    acceptableMin: number;
    acceptableMax: number;
    /** Por debajo del acceptableMin = invalid (profundidad insuficiente). */
    invalidReasonShallow: string;
    /** Por arriba del acceptableMax = invalid (profundidad excesiva). */
    invalidReasonTooDeep: string;
  };
  trunk: {
    /** Ángulo desde vertical (absoluto, no relativo al baseline). */
    idealMax: number;
    /** Entre idealMax y warningMax = acceptable. */
    warningMax: number;
    /** Por arriba de warningMax = invalid. */
    invalidReason: string;
  };
  phase: {
    /** Si la rep no vuelve a top completo dentro de N grados del baseline,
     *  fase incompleta → invalid. */
    returnToTopWithinDeg: number;
    invalidReason: string;
  };
}

/** Fases del state machine de squat. Lineal vs oscilante del evaluator
 *  legacy. */
export type SquatPhase =
  | "awaiting_baseline"
  | "standing"
  | "descent"
  | "bottom"
  | "ascent";

// ============================================================================
// Resultados de evaluación — lo que emite el Evaluator cada frame
// ============================================================================

export interface RuleEvaluation {
  ruleIndex: number;
  violationId: string;
  passed: boolean;
  /** Mensaje de feedback actual (vacío si passed). */
  feedback: string;
  /** Valor actual medido (ej: ángulo actual) — para debug overlay. */
  currentValue: number;
}

export interface EvaluationResult {
  /** Se emite un nuevo resultado cada frame. Vacío para fases del squat
   *  evaluator (que no usa el array `rules` legacy). */
  ruleEvaluations: RuleEvaluation[];
  repCount: number;
  /** Fase actual del detector de repeticiones. Union de fases legacy
   *  (rot externa) + fases squat. UI puede mapear ambas a feedback genérico. */
  repPhase:
    | "inicio"
    | "concentrica"
    | "pico"
    | "excentrica"
    | SquatPhase;
  /** Última rep completada, si recién se completó. undefined la mayoría de frames. */
  completedRep?: RepResult;
  /** Solo para squat — emitido cuando el baseline se captura exitosamente.
   *  La UI puede usarlo para transicionar del estado baseline_capture al
   *  countdown. undefined para schemas legacy. */
  baselineSnapshot?: BaselineSnapshot;
  /** Solo para squat — progreso del baseline capture en curso (0..1).
   *  undefined cuando no estamos en awaiting_baseline. */
  baselineProgress?: number;
  /** Valores dev-facing que el evaluator puede exponer al HUD de debug.
   *  Opcional y libre — cada evaluator define qué campos son útiles para
   *  su calibración (ángulo actual, side, dwell timer, etc.). El HUD los
   *  renderea sin conocer semántica: `label: value`. */
  debug?: Record<string, number | string>;
}

export interface RepResult {
  n: number;
  /** Ángulo máximo absoluto observado durante la rep. Para rot externa
   *  es el ángulo del codo en pico; para squat es la flexión máxima de
   *  knee desde baseline. La semántica depende del evaluator. */
  angleMax: number;
  durationMs: number;
  /** Score 0..1. Para rot externa: basado en cuántas reglas se mantuvieron.
   *  Para squat: derivado del nivel (ideal=1.0, acceptable=0.7, invalid=0.0). */
  quality: number;
  /** IDs de las violaciones que ocurrieron durante esta rep. */
  violations: string[];
  /** Ángulo del pivot al entrar a concentrica (frame de inicio de la rep).
   *  Se usa con angleMax para calcular prominence. Para squat = baseline_knee. */
  angleAtStart: number;
  /** angleMax - angleAtStart. Amplitud real del movimiento de la rep. */
  prominence: number;
  /** True si la rep no tuvo ninguna violación de las reglas del schema.
   *  Definición técnica binaria. Para squat, derivado: level !== 'invalid'. */
  valid: boolean;
  // ── Campos opcionales squat (presentes solo cuando el evaluator es SquatEvaluator) ──
  /** Clasificación 3-level. undefined para rot externa (que usa solo `valid`). */
  level?: "ideal" | "acceptable" | "invalid";
  /** Detalle por fase — solo squat. undefined para rot externa. */
  squatDetails?: {
    kneeFlexionMax: number;
    trunkLeanMax: number;
    hipFlexionMax: number;
    descentMs: number;
    bottomHoldMs: number;
    ascentMs: number;
    /** Lista de tags semánticos del por qué del nivel asignado.
     *  Ejemplos: "depth_target", "trunk_warning", "tempo_critical_descent". */
    reasons: string[];
  };
}

// ── Interface común para evaluators (rot externa + squat) ─────────────────

/** Interface compartida que SesionCamara consume — abstrae el evaluator
 *  específico (ExerciseEvaluator legacy o SquatEvaluator) detrás de un
 *  contrato común. La factory `createEvaluator(schema)` decide cuál
 *  instanciar según schema.type. */
export interface Evaluator {
  evaluate(pose: PoseResult): EvaluationResult;
  reset(): void;
  /** Versión semántica del evaluator concreto. Se persiste en
   *  pose_analysis.detector.evaluator_version. Cada evaluator define la
   *  suya (ExerciseEvaluator → "iter5", SquatEvaluator → "iter1") y
   *  SesionCamara la lee desde acá en lugar de hardcodearla. */
  readonly version: string;
}

export interface SessionResult {
  exerciseId: string;
  startedAt: string; // ISO
  durationSec: number;
  series: {
    n: number;
    reps: RepResult[];
  }[];
  clientInfo: {
    fpsAvg: number;
    model: string;
  };
}
